import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import {
  hideW3WPopover,
  showW3WPopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import { DEFAULT_ZOOM_LEVEL } from '@rsos/maps/constants';
import { GOOGLE } from '@rsos/maps/constants/mapProviders';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import { captureException } from '@rsos/utils/sentry';
import usePrevious from '@rsos/utils/usePrevious';
import W3WLayer from '../RSOSLayers/W3WLayer';
import storeLayers from '../storeLayers';
import { getW3WIconProps } from './utils';

const W3WGL = ({ map, overlay }) => {
  const dispatch = useDispatch();
  const what3words = useSelector(state => state.what3words?.searched);
  const zoomLevel = useRef(DEFAULT_ZOOM_LEVEL);
  const prevW3W = usePrevious(what3words);

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);
  const prevMapInstance = usePrevious(map.mapInstance);

  const hasGoogleMapIdChanged = useMemo(() => {
    // If the map provider isn't GOOGLE, return false immediately.
    if (mapProvider !== GOOGLE) return false;

    const prevMapId = prevMapInstance?.mapId;
    const currentMapId = map.mapInstance?.mapId;

    // Check if both id's exist and have changed.
    return Boolean(prevMapId && currentMapId && prevMapId !== currentMapId);
  }, [mapProvider, prevMapInstance?.mapId, map.mapInstance?.mapId]);

  const handleHoverW3WLocation = useCallback(
    ({ w3wData }) => {
      if (map) {
        if (w3wData) {
          dispatch(showW3WPopover(w3wData));
        } else {
          dispatch(hideW3WPopover(null));
          overlay.setCursorDefault();
        }
      }
    },
    [dispatch, map, overlay],
  );

  /** define layers and properties used to draw markers and circles */
  useEffect(() => {
    const w3wIconLayerProps = getW3WIconProps();
    const w3wLayer = new W3WLayer({
      data: what3words?.coordinates ? [what3words] : [],
      w3wIconLayerProps,
      autoHighlight: true,
      onHover: d => {
        return handleHoverW3WLocation({ w3wData: d.object });
      },
    });
    if (overlay) {
      storeLayers.save(w3wLayer);
      overlay.setLayers(storeLayers.getAll());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [what3words, overlay]);

  /** manage zooming and panning */
  useEffect(() => {
    let zoomChangeListener;

    if (map.mapInstance) {
      if (what3words?.coordinates && !isEqual(what3words, prevW3W)) {
        const coordinates = what3words.coordinates;

        panMapTo({
          mapProvider: GOOGLE,
          mapInstance: map.mapInstance,
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          zoom: null,
        });
      } else {
        // Reset zoom level when transitioning to all view
        zoomLevel.current = DEFAULT_ZOOM_LEVEL;
      }

      zoomChangeListener = map.mapInstance.addListener('zoom_changed', () => {
        zoomLevel.current = map.mapInstance.getZoom();
      });

      return () => {
        if (zoomChangeListener) {
          try {
            window.google.maps.event.removeListener(zoomChangeListener);
          } catch (error) {
            captureException(error);
          }
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, what3words, hasGoogleMapIdChanged]);

  return <></>;
};

export default React.memo(W3WGL);
